import { attributes, type HtmlValue, html } from 'BomdisoftComponents/html/index';
import { renderMenu, renderMenuDivider, renderMenuItem } from 'BomdisoftComponents/menu/render';
import { renderTooltip } from 'BomdisoftComponents/tooltip/render';
import type { PageLink } from 'BomdisoftComponents/types';
import { getPathname } from '@bomdisoft/utilities/functions';

export type NavigationPageOptions = {
  action?: (event: MouseEvent) => void;
  icon?: HtmlValue;
  link?: PageLink;
  name: string;
  type?: 'Page' | 'Divider';
};

export type NavigationRouteOptions = {
  action?: (event: MouseEvent) => void;
  icon?: HtmlValue;
  iconOnly?: boolean;
  isActive?: boolean;
  link?: PageLink;
  name?: string;
  pages?: readonly NavigationPageOptions[];
  testId: string;
};

export type NavigationButtonOptions = {
  currentPage: string;
  isMenuOpen?: boolean;
  menuId: string;
  onlyShowIconsWhenActive?: boolean;
  route: NavigationRouteOptions;
  showOnlyIconsOnSmallScreens?: boolean;
  tooltipId: string;
};

export const isActiveRoute = (route: { link?: PageLink; pages?: readonly { link?: PageLink }[] }, currentPage: string) => {
  if (route?.link && getPathname(route.link) === currentPage) return true;
  if (!route.pages) return false;

  for (let i = 0; i < route.pages.length; i++) {
    if (getPathname(route.pages[i].link) === currentPage) return true;
  }

  return false;
};

export const toNavigationItemTestId = (routeName: string | undefined, pageName: string) => `navigation-item-${routeName?.trim().toLowerCase().replaceAll(' ', '-')}-${pageName.trim().toLowerCase().replaceAll(' ', '-')}`;

export const renderNavigationButton = ({ currentPage, isMenuOpen = false, menuId, onlyShowIconsWhenActive = false, route, showOnlyIconsOnSmallScreens = true, tooltipId }: NavigationButtonOptions) => {
  const hasLabel = !route.iconOnly && Boolean(route.name);
  const iconGivesWayToLabel = onlyShowIconsWhenActive && !route.isActive && hasLabel;
  const name = hasLabel ? html`<span${attributes({ class: 'bomdisoft-navigation-button-name', 'data-with-icon': Boolean(route.icon) })}>${route.name}</span>` : '';
  const listItem = html`<li${attributes({
    class: 'bomdisoft-navigation-button',
    'data-active': isActiveRoute(route, currentPage) || route.isActive,
    'data-flexible': showOnlyIconsOnSmallScreens && !route.iconOnly,
    'data-icon-gives-way': iconGivesWayToLabel,
    'data-icon-only': route.iconOnly,
  })}><button${attributes({ 'aria-label': route.name, class: 'bomdisoft-navigation-button-trigger', type: 'button' })}><span class="bomdisoft-navigation-button-route"><span aria-hidden="true" class="bomdisoft-navigation-button-icon">${route.icon}</span>${name}</span></button></li>`;
  const button = route.iconOnly && !route.pages ? renderTooltip({ children: listItem, id: tooltipId, title: route.name }) : listItem;
  const content = route.link ? html`<a${attributes({ class: 'bomdisoft-navigation-button-link', 'data-testid': route.testId, href: route.link.url })}>${button}</a>` : button;
  if (!route.pages) return content;
  const rows = route.pages.map(page => {
    if (page.type == 'Divider') return renderMenuDivider();
    const icon = page.icon ? html`<span aria-hidden="true" class="bomdisoft-navigation-button-menu-icon">${page.icon}</span>` : '';
    return page.link ? renderMenuItem({ children: html`${icon}${page.name}`, selected: getPathname(page.link) === currentPage }) : renderMenuItem({ children: html`${icon}${page.name}`, testId: toNavigationItemTestId(route.name, page.name) });
  });
  return html`${content}${renderMenu({ children: rows, id: menuId, open: isMenuOpen, testId: `menu-dialog-for-${route.testId}` })}`;
};
